import React, { useEffect, useState } from "react";
import { X, Info } from "lucide-react";
import { api } from "../lib/api";

/**
 * Cut Reminder popup — appears on the dashboard when the user's latest
 * workout's tonnage is lower than their previous same-day workout AND
 * their bodyweight / circumferences are trending down.
 *
 * The message: a small strength drop during a cut is normal and doesn't
 * mean muscle loss. Pure encouragement, no math, no graphs.
 *
 * Once dismissed, the workout_id is persisted on the user so it doesn't
 * re-pop on subsequent dashboard visits.
 */
export default function CutReminder() {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/progress/cut-reminder");
                if (!cancelled && data?.show) setData(data);
            } catch (_) { /* non-blocking */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const close = async () => {
        setDismissed(true);
        if (data?.workout_id) {
            try { await api.post("/progress/cut-reminder/dismiss", { workout_id: data.workout_id }); } catch (_) { /* ignore */ }
        }
    };

    if (!data || dismissed) return null;

    return (
        <div
            className="fixed inset-x-0 bottom-24 z-[60] mx-auto max-w-md px-3 sm:bottom-4"
            role="status"
            aria-live="polite"
            data-testid="cut-reminder-popup"
        >
            <div className="relative border-2 border-accent-red bg-[#0a0a0a]/95 p-5 shadow-2xl backdrop-blur-md">
                <button
                    onClick={close}
                    aria-label="Dismiss"
                    data-testid="cut-reminder-close"
                    className="absolute right-2 top-2 rounded-full p-1 text-white/55 hover:text-white"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="flex items-start gap-3">
                    <div className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md bg-accent-red/15 text-accent-red">
                        <Info className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="label-eyebrow text-accent-red">Heads up</div>
                        <h3 className="mt-0.5 font-display text-lg font-black uppercase leading-tight text-white">
                            Don&apos;t sweat the strength dip.
                        </h3>
                        <p className="mt-2 text-[13px] leading-relaxed text-white/90">
                            Your tonnage on <span className="font-bold text-white">{data.day_name}</span> is down a bit
                            {data.weight_drop_kg ? <> and your bodyweight is trending down ({data.weight_drop_kg} kg)</> : null}
                            {data.circumference_drop_cm ? <> with circumferences shrinking</> : null}.
                            That&apos;s normal during a cut — losing a little strength does <span className="italic">not</span> mean
                            losing muscle. Keep your protein high, keep the reps challenging in the 4–8 range, and the bar
                            will move again once you re-feed.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
